'use client'

import { ButtonGroup } from '@/components/ui/button-group'
import { Button } from '@/components/ui/button'
import { MIXPANEL_CLIENT_EVENT_NAMES } from '@/lib/mixpanel/constant'
import sendToBothAnalytics from '@/lib/mixpanel/sendToBothAnalytics'
import { Monitor, Smartphone } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useMedia } from 'react-use'
import { cn } from '@/lib/utils'

type TDeviceToggleProps = {
  userId?: string
  onDeviceChange: (device: 'desktop' | 'mobile') => void
  initialDevice?: 'desktop' | 'mobile'
}

/**
 * Device view toggle component for switching between desktop and mobile preview
 * @param userId - User ID for analytics tracking
 * @param onDeviceChange - Callback when device view changes
 * @param initialDevice - Initial device view
 * @returns Device toggle buttons
 */
export function DeviceToggle({
  userId,
  onDeviceChange,
  initialDevice = 'desktop',
}: TDeviceToggleProps) {
  const [deviceView, setDeviceView] = useState<'desktop' | 'mobile'>(
    initialDevice,
  )
  const isWide = useMedia('(min-width: 480px)', true)

  /**
   * Auto-adjust device view based on viewport width
   */
  useEffect(() => {
    const newDevice = isWide ? 'desktop' : 'mobile'
    setDeviceView(newDevice)
    onDeviceChange(newDevice)
  }, [isWide, onDeviceChange])

  /**
   * Handle device view change
   */
  const handleDeviceChange = (device: 'desktop' | 'mobile') => {
    setDeviceView(device)
    onDeviceChange(device)

    const eventName =
      device === 'desktop'
        ? MIXPANEL_CLIENT_EVENT_NAMES.CLICK_PREVIEW_TEMPLATE_SWITCH_DESKTOP
        : MIXPANEL_CLIENT_EVENT_NAMES.CLICK_PREVIEW_TEMPLATE_SWITCH_MOBILE

    sendToBothAnalytics(eventName, {}, userId || '')
  }

  return (
    <ButtonGroup className="bg-white/[0.06] p-0.5">
      <Button
        variant="ghost"
        size="icon"
        className={cn(
          'h-8 w-8 rounded-[6px]',
          deviceView === 'desktop'
            ? 'bg-white/[0.08] text-white opacity-100'
            : 'text-white opacity-60 hover:bg-white/[0.08] hover:opacity-100',
        )}
        onClick={() => {
          handleDeviceChange('desktop')
        }}
      >
        <Monitor className="w-4 h-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className={cn(
          'h-8 w-8 rounded-[6px]',
          deviceView === 'mobile'
            ? 'bg-white/[0.08] text-white opacity-100'
            : 'text-white opacity-60 hover:bg-white/[0.08] hover:opacity-100',
        )}
        onClick={() => {
          handleDeviceChange('mobile')
        }}
      >
        <Smartphone className="w-4 h-4" />
      </Button>
    </ButtonGroup>
  )
}
